import React from 'react';
import store from 'store/Store';
import actions from 'actions/Actions';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Flex, Icon, Link, Text
} from 'components/ui';
import { colors, radii, space } from 'style/config';
import { HEADER_VALUE } from 'constants/authentication';
import localeUtils from 'utils/LanguageLocale';
import basketUtils from 'utils/Basket';
import HelpersUtils from 'utils/Helpers';
import userUtils from 'utils/User';
import skuUtils from 'utils/Sku';
import AddToBasketModalEN from 'components/GlobalModals/AddToBasketModal/locales/AddToBasketModal_en_US';
import AddToBasketModalFR from 'components/GlobalModals/AddToBasketModal/locales/AddToBasketModal_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(AddToBasketModalEN, AddToBasketModalFR);

const FREE_SHIPPING_THRESHOLD = skuUtils.MINIMUM_PRICE_FOR_FREE_SHIPPING;

const openSignInModal = () => {
    store.dispatch(actions.showAddToBasketModal({ isOpen: false }));
    store.dispatch(actions.showSignInModal({ isOpen: true, extraParams: { headerValue: HEADER_VALUE.USER_CLICK } }));
};

/**
 * Renders the Free Shipping progress bar content. Used in the Add To Basket modal and other host modals (e.g. BasketMobile).
 *
 * @param {object} props
 * @param {object} [props.basket] Basket payload (expects `subtotal` when signed out).
 * @param {{challengerOne?: boolean, challengerTwo?: boolean}} [props.freeShippingProgressBarExperience]
 * @param {Function} [props.onSignInClick] Optional override for the Sign In link handler.
 *   When provided, replaces the default behavior (close ATB modal + open sign-in modal).
 *   Use this when the component is rendered inside a host modal that also needs to be closed (e.g. BasketMobile).
 */
const FreeShippingProgressBar = ({ basket, freeShippingProgressBarExperience, onSignInClick }) => {
    const isChallengerTwo = freeShippingProgressBarExperience?.challengerTwo;
    const isLoggedIn = !userUtils.isAnonymous();

    if (isLoggedIn) {
        return (
            <Box
                paddingBottom={4}
                paddingTop={0}
                paddingX={[6, 0]}
                {...styles.outerBox}
            >
                <Text
                    is='p'
                    textAlign='center'
                    fontSize='base'
                >
                    <Flex {...styles.statusFlex}>
                        <Icon
                            name='circleCheck'
                            size={18}
                            color='green'
                            {...styles.statusIcon}
                        />
                        {getText('freeShippingThresholdMet')} <strong>{getText('fastFreeShippingFull')}</strong>{' '}
                        {getText(isChallengerTwo ? 'freeShippingBeautyInsiderOnAllOrders' : 'freeShippingBeautyInsiderNoMinSpend')}
                    </Flex>
                </Text>
            </Box>
        );
    }

    const subtotal = basket?.subtotal ? Number(basketUtils.removeCurrency(basket.subtotal)) : 0;
    const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);
    const percentage = Math.min(100, (subtotal / FREE_SHIPPING_THRESHOLD) * 100);
    const amountToFreeShipping = HelpersUtils.formatPrice(`$${remaining.toFixed(2)}`);
    const thresholdLabel = HelpersUtils.formatPrice(`$${FREE_SHIPPING_THRESHOLD}`);
    const thresholdMet = remaining === 0;

    return (
        <Box
            paddingBottom={4}
            paddingTop={0}
            paddingX={[6, 5]}
            {...styles.outerBox}
        >
            {thresholdMet ? (
                <Text
                    is='p'
                    textAlign='center'
                    fontSize='base'
                    marginBottom={2}
                >
                    <Flex
                        alignItems='center'
                        justifyContent='center'
                        gap={1}
                    >
                        <Icon
                            name='circleCheck'
                            size={18}
                            color='green'
                        />
                        {getText('freeShippingThresholdMet')} <strong>{getText('fastFreeShippingFull')}</strong>
                    </Flex>
                </Text>
            ) : (
                <>
                    {/* Mobile: first line shows amount and shipping label only */}
                    <Text
                        is='p'
                        textAlign='center'
                        fontSize='base'
                        marginBottom={2}
                        display={['block', 'none']}
                    >
                        {getText('freeShippingAdd')} <strong>{amountToFreeShipping}</strong> {getText('freeShippingFor')}{' '}
                        <strong>{getText('fastFreeShippingFull')}</strong>
                    </Text>
                    {/* Desktop: all text in one line */}
                    <Text
                        is='p'
                        textAlign='center'
                        fontSize='base'
                        marginBottom={2}
                        display={['none', 'block']}
                    >
                        {getText('freeShippingAdd')} <strong>{amountToFreeShipping}</strong> {getText('freeShippingFor')}{' '}
                        <strong>{getText('fastFreeShippingFull')}</strong> {getText('freeShippingOr')}{' '}
                        <Link
                            color='blue'
                            display='inline'
                            underline={true}
                            onClick={onSignInClick || openSignInModal}
                        >
                            {getText('freeShippingSignIn')}
                        </Link>{' '}
                        {getText(isChallengerTwo ? 'freeShippingOnAllOrders' : 'freeShippingNoMinSpend')}
                    </Text>
                </>
            )}
            <Flex
                alignItems='center'
                gap={2}
                css={styles.progressBarWrapper}
            >
                <Box css={styles.track}>
                    <Box
                        css={styles.fill}
                        style={{ width: `${percentage}%` }}
                        role='progressbar'
                        aria-valuenow={Math.round(percentage)}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={getText('freeShippingProgressAriaLabel', [Math.round(percentage), thresholdLabel])}
                    />
                </Box>
                <Text
                    is='span'
                    fontSize='sm'
                    fontWeight='bold'
                    css={styles.thresholdLabel}
                >
                    {thresholdLabel}
                </Text>
            </Flex>
            {/* Mobile: sign-in link below the progress bar */}
            {!thresholdMet && (
                <Text
                    is='p'
                    textAlign='center'
                    fontSize='base'
                    marginTop={2}
                    display={['block', 'none']}
                >
                    {getText('freeShippingOr')}{' '}
                    <Link
                        color='blue'
                        display='inline'
                        underline={true}
                        onClick={onSignInClick || openSignInModal}
                    >
                        {getText('freeShippingSignIn')}
                    </Link>{' '}
                    {getText(isChallengerTwo ? 'freeShippingOnAllOrders' : 'freeShippingNoMinSpend')}
                </Text>
            )}
        </Box>
    );
};

const styles = {
    track: {
        flex: 1,
        height: space[2],
        backgroundColor: colors.midGray,
        borderRadius: radii.full,
        position: 'relative',
        overflow: 'hidden'
    },
    fill: {
        position: 'absolute',
        top: 0,
        left: 0,
        height: '100%',
        backgroundColor: colors.red,
        borderRadius: radii.full,
        transition: 'width 0.3s ease-in-out'
    },
    progressBarWrapper: {
        maxWidth: space[9] * 3 + space[7],
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    thresholdLabel: {
        whiteSpace: 'nowrap',
        color: colors.red
    },
    outerBox: {
        marginLeft: [`-${space[4]}px`, `-${space[5]}px`],
        marginRight: [`-${space[4]}px`, `-${space[5]}px`],
        marginBottom: `${space[5]}px`,
        borderBottom: `1px solid ${colors.lightGray}`
    },
    statusFlex: {
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        display: ['inline', 'flex']
    },
    statusIcon: {
        marginRight: [`${space[1]}px`, 0]
    }
};

export default wrapFunctionalComponent(FreeShippingProgressBar, 'FreeShippingProgressBar');
